import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase, functions } from '../../../shared/lib/supabase';
import type { Product } from '../../../shared/types';

/**
 * Hook para listar todos os produtos
 */
export function useProducts() {
  return useQuery({
    queryKey: ['products'],
    queryFn: async () => {
      // First attempt with product_form relationship
      const { data, error } = await supabase
        .from('products')
        .select('*, n8n_instance:n8n_instances(*), product_form:product_forms(*)')
        .order('created_at', { ascending: false });

      // If PostgREST complains about missing relationships (PGRST200/204), retry without the relationship
      if (error) {
        const isRelationshipError = (error?.code === 'PGRST200') || (error?.message && (error.message.includes('Could not find a relationship') || error.message.includes('Could not find table')));
        if (isRelationshipError) {
          console.warn('Relationship product_forms missing in PostgREST cache; retrying products select without product_form');
          const { data: fallbackData, error: fallbackError } = await supabase
            .from('products')
            .select('*, n8n_instance:n8n_instances(*)')
            .order('created_at', { ascending: false });
          if (fallbackError) throw fallbackError;
          return fallbackData as Product[];
        }
        throw error;
      }

      return data as Product[];
    },
  });
}

/**
 * Hook para criar um novo produto
 */
export function useCreateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (product: Omit<Product, 'id' | 'created_at' | 'updated_at' | 'n8n_instance'>) => {
      const { data, error } = await supabase
        .from('products')
        .insert([product])
        .select()
        .single();

      if (error) throw error;
      return data as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}

/**
 * Hook para atualizar um produto existente
 * Também sincroniza mudanças para workflows clonados
 */
export function useUpdateProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Product> & { id: string }) => {
      // Update product in database
      const { data, error } = await supabase
        .from('products')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // If name or description changed, sync to cloned workflows
      if (updates.name || updates.description !== undefined) {
        try {
          await functions.syncProductChanges(
            id,
            updates.name || data.name,
            updates.description !== undefined ? updates.description : data.description
          );
        } catch (syncError) {
          console.error('Failed to sync product changes to workflows:', syncError);
          // Don't fail the whole mutation, just log the error
        }
      }

      return data as Product;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['client-products'] });
    },
  });
}

/**
 * Hook para deletar um produto
 */
export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
  });
}
